import { useEffect, useMemo, useRef, useState } from "react";
import { useIsMutating, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  api,
  type ActionResult,
  type EditableScene,
  type EditableStoryboard,
  type KeywordDetail,
  type SceneImageMotionView,
  type SceneTransitionView,
} from "../../lib/api.js";
import { buildMaestroBrief, copyToClipboard } from "../../lib/maestro-brief.js";

const TRANSITIONS: SceneTransitionView[] = ["cut", "fade", "slide-left", "slide-up"];
const MOTIONS: SceneImageMotionView[] = ["static", "subtle-zoom", "pan-left", "pan-right", "ken-burns"];

function relTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return "";
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function blankScene(index: number): EditableScene {
  return {
    index,
    durationSec: 5,
    imagePrompt: "",
    hebrewCaption: "",
    narration: "",
    transition: "cut",
    imageMotion: "subtle-zoom",
    visualNote: null,
  };
}

/**
 * Video Studio — edit the storyboard that drives the Remotion composer, see each
 * scene's generated image, preview the rendered MP4, and re-render. Editing is
 * deterministic (no LLM); a re-render reuses existing scene assets and only
 * re-composes, so caption/timing/motion tweaks are cheap. "Regenerate all" forces
 * fresh scene images from the edited prompts.
 */
export function VideoStudioTab({
  keywordId,
  detail,
  onAfterAction,
}: {
  keywordId: string;
  detail: KeywordDetail;
  onAfterAction: () => void;
}) {
  const saved = (detail.keyword.storyboard as EditableStoryboard | null) ?? null;
  const savedJson = JSON.stringify(saved);

  const [draft, setDraft] = useState<EditableStoryboard | null>(saved);
  const [dirty, setDirty] = useState(false);
  const [banner, setBanner] = useState<{ kind: "ok" | "error"; text: string } | null>(null);

  // Re-sync from the server whenever the saved board changes and we have no
  // pending edits (e.g. after a save/refetch or a fresh generate).
  useEffect(() => {
    if (!dirty) setDraft(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedJson]);

  const qc = useQueryClient();
  const sbRunning = useIsMutating({ mutationKey: ["gen", keywordId, "storyboard"] }) > 0;

  // Server-side render job — the source of truth for "is a render happening",
  // independent of this browser tab. Polls fast while rendering, slow when idle
  // (so a render started elsewhere / before a refresh still shows up).
  const statusQuery = useQuery({
    queryKey: ["pipeline", "keyword", keywordId, "viral-status"],
    queryFn: () => api.getViralStatus(keywordId),
    refetchInterval: (q) => (q.state.data?.status === "rendering" ? 3_000 : 20_000),
  });
  const viralStatus = statusQuery.data;
  const rendering = viralStatus?.status === "rendering";

  const assetsQuery = useQuery({
    queryKey: ["pipeline", "keyword", keywordId, "assets"],
    queryFn: () => api.listKeywordAssets(keywordId),
    refetchInterval: rendering ? 5_000 : 30_000,
  });
  const assets = assetsQuery.data?.assets ?? [];

  // React to render completion: refresh assets + surface a banner once, on the
  // rendering→done/error transition.
  const prevStatus = useRef<string | undefined>(viralStatus?.status);
  useEffect(() => {
    const prev = prevStatus.current;
    prevStatus.current = viralStatus?.status;
    if (prev === "rendering" && viralStatus?.status === "done") {
      const s = viralStatus.summary;
      setBanner({ kind: "ok", text: s ? `Render complete · ${s.scenes} scenes · ${(s.totalSec ?? 0).toFixed?.(0) ?? s.totalSec}s · ${((s.bytes ?? 0) / 1e6).toFixed(1)}MB` : "Render complete" });
      void qc.invalidateQueries({ queryKey: ["pipeline", "keyword", keywordId, "assets"] });
      onAfterAction();
    } else if (prev === "rendering" && viralStatus?.status === "error") {
      setBanner({ kind: "error", text: viralStatus.error ?? "Render failed" });
    }
  }, [viralStatus?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const sceneImageByIndex = useMemo(() => {
    const m = new Map<number, string>();
    for (const a of assets) {
      if (a.kind === "scene_image" && a.sectionIndex != null && a.sanityUrl) m.set(a.sectionIndex, a.sanityUrl);
    }
    return m;
  }, [assets]);
  const finalVideo = useMemo(() => assets.find((a) => a.kind === "viral_final" && a.sanityUrl) ?? null, [assets]);

  // Import an externally-produced video (e.g. a Maestro render) as a keyword asset.
  const importFileRef = useRef<HTMLInputElement | null>(null);
  const [importKind, setImportKind] = useState<"short_vertical" | "long_horizontal" | "square_feed" | "viral_final">("short_vertical");
  // Background music on by default — a soft Hebrew-friendly ambient bed mixes
  // under the narration at low volume. Untick to render narration-only.
  const [music, setMusic] = useState(true);
  const importVideo = useMutation({
    mutationFn: (file: File) => api.importKeywordVideo(keywordId, file, importKind),
    onSuccess: () => {
      if (importFileRef.current) importFileRef.current.value = "";
      void qc.invalidateQueries({ queryKey: ["pipeline", "keyword", keywordId, "assets"] });
    },
  });

  const done = (r: ActionResult) => {
    setBanner({ kind: "ok", text: r.summary ?? "Done" });
    onAfterAction();
  };
  const fail = (e: Error) => setBanner({ kind: "error", text: e.message });

  const save = useMutation({
    mutationFn: () => api.saveStoryboard(keywordId, draft as EditableStoryboard),
    onSuccess: (r) => { setDirty(false); done(r); },
    onError: fail,
  });
  const genStoryboard = useMutation({
    mutationKey: ["gen", keywordId, "storyboard"],
    mutationFn: () => api.generateStoryboard(keywordId),
    onSuccess: done,
    onError: fail,
  });
  // Render kicks off a BACKGROUND job and returns immediately; progress comes
  // from the status poll (so it survives refresh + never holds a connection).
  const startedRender = (r: ActionResult) => {
    setBanner({ kind: "ok", text: r.summary ?? "Render started" });
    void qc.invalidateQueries({ queryKey: ["pipeline", "keyword", keywordId, "viral-status"] });
  };
  const reRender = useMutation({
    mutationFn: () => api.generateViral(keywordId, { force: false, music }),
    onSuccess: startedRender,
    onError: fail,
  });
  const regenAll = useMutation({
    mutationFn: () => api.generateViral(keywordId, { force: true, music }),
    onSuccess: startedRender,
    onError: fail,
  });

  // ── No storyboard yet ──────────────────────────────────────────────────────
  if (!draft) {
    return (
      <div className="space-y-3">
        {banner && <Banner banner={banner} />}
        <div className="rounded-md border border-dashed border-line bg-white p-6 text-center">
          <p className="text-sm text-muted">No storyboard yet. Generate one from the produced article to start editing your video.</p>
          <button
            className="btn-primary mt-3"
            disabled={sbRunning || !detail.keyword.articleMarkdown}
            onClick={() => genStoryboard.mutate()}
            title={!detail.keyword.articleMarkdown ? "Produce an article first" : "Sonnet → JSON storyboard"}
          >
            {sbRunning ? "Generating…" : "Generate storyboard"}
          </button>
        </div>
      </div>
    );
  }

  const update = (patch: Partial<EditableStoryboard>) => { setDraft({ ...draft, ...patch }); setDirty(true); };
  const updateScene = (i: number, patch: Partial<EditableScene>) => {
    const scenes = draft.scenes.map((s, idx) => (idx === i ? { ...s, ...patch } : s));
    setDraft({ ...draft, scenes }); setDirty(true);
  };
  const moveScene = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= draft.scenes.length) return;
    const scenes = [...draft.scenes];
    const a = scenes[i];
    const b = scenes[j];
    if (!a || !b) return;
    scenes[i] = b;
    scenes[j] = a;
    setDraft({ ...draft, scenes }); setDirty(true);
  };
  const removeScene = (i: number) => {
    if (draft.scenes.length <= 3) { setBanner({ kind: "error", text: "A storyboard needs at least 3 scenes." }); return; }
    setDraft({ ...draft, scenes: draft.scenes.filter((_, idx) => idx !== i) }); setDirty(true);
  };
  const addScene = () => {
    if (draft.scenes.length >= 15) { setBanner({ kind: "error", text: "Max 15 scenes." }); return; }
    setDraft({ ...draft, scenes: [...draft.scenes, blankScene(draft.scenes.length)] }); setDirty(true);
  };

  const totalSec = draft.scenes.reduce((s, sc) => s + (Number(sc.durationSec) || 0), 0);
  const busy = save.isPending || reRender.isPending || regenAll.isPending || rendering;
  const stageLabel = viralStatus?.stage ? `Rendering · ${viralStatus.stage}…` : "Rendering…";

  return (
    <div className="space-y-4">
      {banner && <Banner banner={banner} />}

      {/* Final video preview */}
      <section className="rounded-md border border-line bg-white p-3">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold">Rendered video</h3>
          {rendering
            ? <span className="flex items-center gap-1.5 text-2xs text-muted"><span className="inline-block h-2 w-2 animate-pulse rounded-full bg-emerald-500" />{stageLabel}</span>
            : finalVideo && <span className="text-2xs text-muted">rendered {relTime(finalVideo.createdAt)}{finalVideo.byteSize ? ` · ${(finalVideo.byteSize / 1e6).toFixed(1)}MB` : ""}</span>}
        </div>
        {rendering && (
          <p className="mb-2 rounded bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
            {stageLabel} This runs in the background — you can close this panel or refresh; it keeps going and the video appears here when done.
          </p>
        )}
        {finalVideo?.sanityUrl ? (
          <video
            key={finalVideo.sanityUrl}
            src={finalVideo.sanityUrl}
            controls
            className="mx-auto max-h-[420px] rounded"
            style={{ aspectRatio: draft.aspectRatio.replace(":", "/") }}
          />
        ) : (
          <p className="py-6 text-center text-sm text-muted">{rendering ? "Rendering your video…" : "No rendered video yet. Edit the scenes, then Re-render."}</p>
        )}
      </section>

      {/* Import a video produced elsewhere (e.g. Maestro) */}
      <section className="rounded-md border border-line bg-white p-3">
        <div className="mb-2 flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">Import a video</h3>
          <span className="text-2xs text-muted">bring in a clip produced elsewhere (Maestro, etc.)</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            className="input"
            value={importKind}
            disabled={importVideo.isPending}
            onChange={(e) => setImportKind(e.target.value as typeof importKind)}
          >
            <option value="short_vertical">Short vertical (TikTok / Reels / Shorts)</option>
            <option value="long_horizontal">Long horizontal (YouTube)</option>
            <option value="square_feed">Square (feed)</option>
            <option value="viral_final">Final (studio preview)</option>
          </select>
          <input
            ref={importFileRef}
            type="file"
            accept="video/mp4,video/webm,video/quicktime"
            className="text-xs"
            disabled={importVideo.isPending}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) importVideo.mutate(f); }}
          />
          {importVideo.isPending && <span className="text-2xs text-muted">uploading…</span>}
        </div>
        {importVideo.isError && <p className="mt-2 text-2xs text-danger">{(importVideo.error as Error).message}</p>}
        {importVideo.isSuccess && !importVideo.isPending && (
          <p className="mt-2 text-2xs text-emerald-700">Imported ✓ — attached as a {importKind.replace(/_/g, " ")} asset. It flows into distribution (and shows above if you chose Final).</p>
        )}
      </section>

      {/* Storyboard meta + actions */}
      <section className="rounded-md border border-line bg-white p-3">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Aspect">
            <select className="input" value={draft.aspectRatio} onChange={(e) => update({ aspectRatio: e.target.value as EditableStoryboard["aspectRatio"] })}>
              <option value="9:16">9:16 (Reels/Shorts/TikTok)</option>
              <option value="1:1">1:1 (feed)</option>
              <option value="16:9">16:9 (YouTube)</option>
            </select>
          </Field>
          <div className="text-xs text-muted">
            {draft.scenes.length} scenes · {totalSec}s{draft.modelUsed ? ` · ${draft.modelUsed}` : ""}
            {dirty && <span className="ml-2 text-warn">● unsaved</span>}
          </div>
          <label className="ml-auto flex items-center gap-1.5 self-center text-xs text-muted" title="Mix a soft ambient bed under the narration at low volume">
            <input type="checkbox" checked={music} disabled={busy} onChange={(e) => setMusic(e.target.checked)} />
            Background music
          </label>
          <div className="flex gap-2">
            <button
              className="btn-ghost"
              title="Copy this storyboard as a scene-by-scene brief to build the video in Maestro"
              onClick={async () => {
                const ok = await copyToClipboard(buildMaestroBrief(draft));
                setBanner({ kind: ok ? "ok" : "error", text: ok ? "Storyboard copied — paste it into Maestro." : "Copy blocked by the browser — try again." });
              }}
            >
              Export for Maestro
            </button>
            <button className="btn-primary" disabled={!dirty || busy} onClick={() => save.mutate()}>{save.isPending ? "Saving…" : "Save"}</button>
            <button className="btn-secondary" disabled={busy || dirty} title={dirty ? "Save your edits first" : "Reuse scene images, re-compose with current captions/timing (cheap)"} onClick={() => reRender.mutate()}>
              {rendering ? "Rendering…" : "Re-render"}
            </button>
            <button className="btn-ghost" disabled={busy || dirty} title={dirty ? "Save your edits first" : "Regenerate scene images from prompts, then render (~$1.10)"} onClick={() => regenAll.mutate()}>
              {rendering ? "…" : "Regenerate all"}
            </button>
          </div>
        </div>
        <div className="mt-3 grid gap-2">
          <Field label="Hook"><input className="input" dir="auto" value={draft.hook} onChange={(e) => update({ hook: e.target.value })} /></Field>
          <Field label="CTA"><input className="input" dir="auto" value={draft.cta ?? ""} onChange={(e) => update({ cta: e.target.value || null })} placeholder="closing call-to-action overlay" /></Field>
        </div>
      </section>

      {/* Scenes */}
      <section className="space-y-3">
        {draft.scenes.map((s, i) => (
          <div key={i} className="rounded-md border border-line bg-white p-3">
            <div className="flex gap-3">
              {/* thumbnail */}
              <div className="w-28 shrink-0">
                {sceneImageByIndex.get(i) ? (
                  <img src={sceneImageByIndex.get(i)} alt={`scene ${i + 1}`} className="w-full rounded border border-line object-cover" style={{ aspectRatio: draft.aspectRatio.replace(":", "/") }} />
                ) : (
                  <div className="flex w-full items-center justify-center rounded border border-dashed border-line text-2xs text-muted" style={{ aspectRatio: draft.aspectRatio.replace(":", "/") }}>no image</div>
                )}
              </div>
              {/* fields */}
              <div className="min-w-0 flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="pill pill-muted font-mono text-2xs">scene {i + 1}</span>
                  <div className="ml-auto flex items-center gap-1">
                    <IconBtn title="Move up" disabled={i === 0} onClick={() => moveScene(i, -1)}>↑</IconBtn>
                    <IconBtn title="Move down" disabled={i === draft.scenes.length - 1} onClick={() => moveScene(i, 1)}>↓</IconBtn>
                    <IconBtn title="Delete scene" onClick={() => removeScene(i)}>✕</IconBtn>
                  </div>
                </div>
                <Field label="Caption (Hebrew, burned-in)">
                  <input className="input" dir="auto" value={s.hebrewCaption} onChange={(e) => updateScene(i, { hebrewCaption: e.target.value })} />
                </Field>
                <Field label="Narration (voiceover)">
                  <textarea className="input min-h-[48px]" dir="auto" value={s.narration} onChange={(e) => updateScene(i, { narration: e.target.value })} />
                </Field>
                <Field label="Image prompt (English)">
                  <textarea className="input min-h-[48px]" dir="ltr" value={s.imagePrompt} onChange={(e) => updateScene(i, { imagePrompt: e.target.value })} />
                </Field>
                <div className="flex flex-wrap gap-3">
                  <Field label="Duration (s)">
                    <input type="number" min={2} max={15} className="input w-20" value={s.durationSec} onChange={(e) => updateScene(i, { durationSec: Number(e.target.value) })} />
                  </Field>
                  <Field label="Transition">
                    <select className="input" value={s.transition} onChange={(e) => updateScene(i, { transition: e.target.value as SceneTransitionView })}>
                      {TRANSITIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </Field>
                  <Field label="Motion">
                    <select className="input" value={s.imageMotion} onChange={(e) => updateScene(i, { imageMotion: e.target.value as SceneImageMotionView })}>
                      {MOTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </Field>
                </div>
              </div>
            </div>
          </div>
        ))}
        <button className="btn-secondary w-full" onClick={addScene} disabled={draft.scenes.length >= 15}>+ Add scene</button>
      </section>

      <p className="text-2xs text-muted">
        Tip: editing captions, timing, transitions, or motion only needs a <strong>Re-render</strong> (reuses existing scene images, re-composes). Editing an image <em>prompt</em> needs <strong>Regenerate all</strong> to redraw the scene images.
      </p>
    </div>
  );
}

function Banner({ banner }: { banner: { kind: "ok" | "error"; text: string } }) {
  return (
    <div className={`rounded-md px-3 py-2 text-sm ${banner.kind === "ok" ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700"}`}>
      {banner.text}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-0.5 block text-2xs font-medium uppercase tracking-wide text-muted">{label}</span>
      {children}
    </label>
  );
}

function IconBtn({ children, onClick, disabled, title }: { children: React.ReactNode; onClick: () => void; disabled?: boolean; title?: string }) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className="flex h-6 w-6 items-center justify-center rounded border border-line text-xs text-muted hover:bg-line/40 disabled:opacity-30"
    >
      {children}
    </button>
  );
}
